/**
 * Step-up authentication (D-003, §15.1, `STEP_UP_PERMISSIONS`): protected mutations (finance, draws, unmask,
 * cancel, exports) re-authenticate with TOTP or a passkey before they run. `useStepUp().withStepUp(fn, opts)`
 * wraps a mutation: it opens the modal when the permission needs step-up and the window is not active, or when
 * the API answers `step_up_required`, then runs (or retries) the mutation.
 */
import { startAuthentication } from '@simplewebauthn/browser';
import { STEP_UP_PERMISSIONS, type Permission, type StaffSessionDto } from '@windrop/contracts';
import { Button, Field, Input, Modal } from '@windrop/ui';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { identityApi } from '../lib/api/admin';
import { isApiError } from '../lib/api/client';
import { isStepUpActive, useSession } from './session';

export interface StepUpOptions {
  /** Permission the mutation exercises; step-up is required when it is listed in STEP_UP_PERMISSIONS. */
  permission?: Permission;
  /** Why the step-up is needed — shown in the modal so the operator knows what they are approving. */
  reason?: string;
  /** Force the modal even for permissions outside STEP_UP_PERMISSIONS. */
  force?: boolean;
}

export interface StepUpContextValue {
  /** Runs `fn` after step-up when required. Rejects with `StepUpCancelled` when the operator closes the modal. */
  withStepUp: <T>(fn: () => Promise<T>, opts?: StepUpOptions) => Promise<T>;
  /** Opens the modal directly; resolves with the refreshed session or null when cancelled. */
  requestStepUp: (reason?: string) => Promise<StaffSessionDto | null>;
  stepUpActive: boolean;
  needsStepUp: (permission: Permission) => boolean;
}

export class StepUpCancelled extends Error {
  constructor() {
    super('Step-up cancelled');
    this.name = 'StepUpCancelled';
  }
}

const StepUpContext = createContext<StepUpContextValue | null>(null);

interface Pending {
  reason: string | undefined;
  resolve: (session: StaffSessionDto | null) => void;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { session, setSession } = useSession();
  const [pending, setPending] = useState<Pending | null>(null);
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'totp' | 'passkey' | null>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const stepUpActive = isStepUpActive(session);

  const requestStepUp = useCallback((reason?: string) => {
    return new Promise<StaffSessionDto | null>((resolve) => {
      setTotp('');
      setError(null);
      setPending({ reason, resolve });
    });
  }, []);

  const finish = useCallback(
    (result: StaffSessionDto | null) => {
      if (result) setSession(result);
      pending?.resolve(result);
      setPending(null);
      setBusy(null);
    },
    [pending, setSession],
  );

  const submitTotp = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(totp)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy('totp');
    setError(null);
    try {
      finish(await identityApi.stepUp({ totp }));
    } catch (err) {
      setBusy(null);
      setError(isApiError(err) ? err.message : 'The code was not accepted. Try again.');
    }
  };

  const usePasskey = async () => {
    setBusy('passkey');
    setError(null);
    try {
      const { options } = await identityApi.stepUpPasskeyOptions();
      const assertion = await startAuthentication({
        optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      finish(await identityApi.stepUp({ passkeyAssertion: assertion as unknown as Record<string, unknown> }));
    } catch (err) {
      setBusy(null);
      setError(
        isApiError(err) ? err.message : 'The passkey was not accepted. Use your authenticator code instead.',
      );
    }
  };

  const needsStepUp = useCallback(
    (permission: Permission) =>
      STEP_UP_PERMISSIONS.includes(permission) && !isStepUpActive(sessionRef.current),
    [],
  );

  const withStepUp = useCallback(
    async <T,>(fn: () => Promise<T>, opts: StepUpOptions = {}): Promise<T> => {
      const required = opts.force || (opts.permission ? needsStepUp(opts.permission) : false);
      if (required) {
        const result = await requestStepUp(opts.reason);
        if (!result) throw new StepUpCancelled();
      }
      try {
        return await fn();
      } catch (err) {
        if (isApiError(err) && err.code === 'step_up_required') {
          const result = await requestStepUp(opts.reason ?? err.message);
          if (!result) throw new StepUpCancelled();
          return await fn();
        }
        throw err;
      }
    },
    [needsStepUp, requestStepUp],
  );

  const value = useMemo<StepUpContextValue>(
    () => ({ withStepUp, requestStepUp, stepUpActive, needsStepUp }),
    [withStepUp, requestStepUp, stepUpActive, needsStepUp],
  );

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <Modal
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) finish(null);
        }}
        title="Confirm it is you"
        description={
          pending?.reason
            ? `${pending.reason} This action needs a fresh verification before it runs.`
            : 'This action needs a fresh verification before it runs.'
        }
      >
        <form onSubmit={submitTotp} className="wd-stack" data-testid="step-up-form">
          <Field label="Authenticator code" required hint="Six digits from your authenticator app.">
            <Input
              preset="otp"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              autoFocus
            />
          </Field>
          {error ? (
            <p role="alert" className="text-error">
              {error}
            </p>
          ) : null}
          <div className="wd-btn-group">
            <Button type="submit" loading={busy === 'totp'} loadingLabel="Checking code">
              Verify and continue
            </Button>
            <Button type="button" variant="secondary" onClick={usePasskey} loading={busy === 'passkey'}>
              Use a passkey
            </Button>
            <Button type="button" variant="quiet" onClick={() => finish(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </StepUpContext.Provider>
  );
}

export function useStepUp(): StepUpContextValue {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error('useStepUp must be used inside <StepUpProvider>');
  return ctx;
}
